/**
 * Penthouse — Tower 12 override
 *
 * Mirrors Weidner Classic’s two-column layout, but the right column uses
 * ApartmentSync’s penthouse mount instead of a mirrored floor-plan card:
 *   <div class="rrac_pent_layout"></div>
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useEditMode,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useRRACScript } from '@vibesites/blocks/hooks/useRRACScript';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import iconKitchen from '../assets/penthouse/cabinet.png';
import iconSteel from '../assets/penthouse/steel.png';
import iconClosets from '../assets/penthouse/closets.png';
import iconBalcony from '../assets/penthouse/balcony.png';

// ============================================================================
// Photo tiles (literals; extractor cannot follow a factory)
// ============================================================================

const photo1Def: SlotGroupDefinition = {
  label: 'Penthouse Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A photo of a real penthouse or top-floor apartment interior at this property — kitchen, living area, or open-concept space with elevated finishes. Do not invent a space.',
    context:
      'Photo 1 of 4 in the EXPERIENCE / PENTHOUSE LIVING gallery on the Penthouse page. Different from photos 2–4.',
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Penthouse Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Another unused penthouse or luxury apartment interior photo from this property — a different room from photo 1.',
    context:
      'Photo 2 of 4 in the EXPERIENCE / PENTHOUSE LIVING gallery on the Penthouse page. Do not reuse photos 1, 3, or 4.',
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Penthouse Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A third unused penthouse or luxury apartment interior photo from this property — a different room from photos 1 and 2.',
    context:
      'Photo 3 of 4 in the EXPERIENCE / PENTHOUSE LIVING gallery on the Penthouse page. Do not reuse photos 1, 2, or 4.',
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Penthouse Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A fourth unused penthouse or luxury apartment interior photo from this property — a different room from photos 1–3.',
    context:
      'Photo 4 of 4 in the EXPERIENCE / PENTHOUSE LIVING gallery on the Penthouse page. Do not reuse photos 1–3.',
  },
};

const amenitiesCtaDef: SlotGroupDefinition = {
  label: 'Amenities CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Our Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Photo tile
// ============================================================================

function PhotoTile({
  path,
  definition,
}: {
  path: string;
  definition: SlotGroupDefinition;
}) {
  const photo = useSlotGroup(path, definition);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;
  const src = photo.image || '';

  if (!src && !isEditing) return null;

  return (
    <div className="penthouse__photo" data-reveal>
      <EditableSlotGroup groupPath={path} definition={definition}>
        {src ? (
          <VibesitesImage
            url={src}
            alt={photo.alt || ''}
            sizes="(min-width: 992px) 12vw, (min-width: 768px) 23vw, 45vw"
            className="penthouse__photo-img"
          />
        ) : (
          <div className="penthouse__photo-placeholder" aria-hidden="true" />
        )}
      </EditableSlotGroup>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function Penthouse() {
  const headline = useContentToken('penthouse.headline');
  const featuresHeading = useContentToken('penthouse.featuresHeading');
  const highlight1Title = useContentToken('penthouse.highlight1Title');
  const highlight2Title = useContentToken('penthouse.highlight2Title');
  const highlight3Title = useContentToken('penthouse.highlight3Title');
  const highlight4Title = useContentToken('penthouse.highlight4Title');
  const amenitiesCta = useSlotGroup('penthouse.amenitiesCta', amenitiesCtaDef);
  const javascriptId = useContentToken('penthouse.javascript_id');
  const configId = useContentToken('penthouse.config_id');

  useRRACScript({
    javascriptId: javascriptId || null,
    configId: configId || null,
    role: 'consumer',
  });

  const sectionRef = useScrollReveal<HTMLElement>();
  const galleryRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 70 });
  const highlightsRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 80 });

  return (
    <section ref={sectionRef} className="penthouse">
      <div className="penthouse__layout">
        {/* ── Left: copy + gallery + features ── */}
        <div className="penthouse__main">
          <div className="penthouse-intro__heading-block">
            <RichTextContent
              path="penthouse.eyebrow"
              label="Eyebrow"
              as="p"
              className="penthouse-intro__eyebrow"
            />
            <EditableToken path="penthouse.headline" as="h2" className="penthouse-intro__headline">
              {headline || 'PENTHOUSE LIVING'}
            </EditableToken>
          </div>

          <div ref={galleryRef} className="penthouse__gallery">
            <PhotoTile path="penthouse.photo1" definition={photo1Def} />
            <PhotoTile path="penthouse.photo2" definition={photo2Def} />
            <PhotoTile path="penthouse.photo3" definition={photo3Def} />
            <PhotoTile path="penthouse.photo4" definition={photo4Def} />
          </div>

          <div className="penthouse-features__block">
            <EditableToken
              path="penthouse.featuresHeading"
              as="h3"
              className="penthouse-features__heading"
            >
              {featuresHeading || 'PENTHOUSE INTERIOR FEATURES'}
            </EditableToken>

            <div ref={highlightsRef} className="penthouse__highlights">
              <div className="penthouse__highlight" data-reveal>
                <a
                  href="/amenities"
                  className="penthouse__highlight-media"
                  aria-label={highlight1Title || 'Newly Designed Kitchens'}
                >
                  <VibesitesImage
                    url={iconKitchen}
                    alt=""
                    width={141}
                    height={96}
                    sizes="140px"
                    className="penthouse__highlight-icon"
                  />
                </a>
                <EditableToken
                  path="penthouse.highlight1Title"
                  as="span"
                  className="penthouse__highlight-title"
                >
                  {highlight1Title || 'Newly Designed Kitchens'}
                </EditableToken>
              </div>

              <div className="penthouse__highlight" data-reveal>
                <a
                  href="/amenities"
                  className="penthouse__highlight-media"
                  aria-label={highlight2Title || 'Stainless-Steel Appliances'}
                >
                  <VibesitesImage
                    url={iconSteel}
                    alt=""
                    width={141}
                    height={102}
                    sizes="140px"
                    className="penthouse__highlight-icon"
                  />
                </a>
                <EditableToken
                  path="penthouse.highlight2Title"
                  as="span"
                  className="penthouse__highlight-title"
                >
                  {highlight2Title || 'Stainless-Steel Appliances'}
                </EditableToken>
              </div>

              <div className="penthouse__highlight" data-reveal>
                <a
                  href="/amenities"
                  className="penthouse__highlight-media"
                  aria-label={highlight3Title || 'Ample Closet Space'}
                >
                  <VibesitesImage
                    url={iconClosets}
                    alt=""
                    width={141}
                    height={81}
                    sizes="140px"
                    className="penthouse__highlight-icon"
                  />
                </a>
                <EditableToken
                  path="penthouse.highlight3Title"
                  as="span"
                  className="penthouse__highlight-title"
                >
                  {highlight3Title || 'Ample Closet Space'}
                </EditableToken>
              </div>

              <div className="penthouse__highlight" data-reveal>
                <a
                  href="/amenities"
                  className="penthouse__highlight-media"
                  aria-label={highlight4Title || 'Private Patio or Balcony'}
                >
                  <VibesitesImage
                    url={iconBalcony}
                    alt=""
                    width={141}
                    height={92}
                    sizes="140px"
                    className="penthouse__highlight-icon"
                  />
                </a>
                <EditableToken
                  path="penthouse.highlight4Title"
                  as="span"
                  className="penthouse__highlight-title"
                >
                  {highlight4Title || 'Private Patio or Balcony'}
                </EditableToken>
              </div>
            </div>

            <RichTextContent
              path="penthouse.featureList"
              label="Feature List"
              as="div"
              className="penthouse__feature-list"
            />

            <EditableSlotGroup
              groupPath="penthouse.amenitiesCta"
              definition={amenitiesCtaDef}
              className="penthouse__amenities-cta-wrap"
            >
              <a
                href={amenitiesCta.url || '/amenities'}
                target={amenitiesCta._target || '_self'}
                className="penthouse__amenities-cta"
              >
                <span>{amenitiesCta.label || 'Explore Our Amenities'}</span>
                <svg
                  className="penthouse__amenities-cta-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 12h14M13 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        {/* ── Right: ApartmentSync penthouse widget ── */}
        <aside className="penthouse__aside">
          <div className="penthouse__aside-meta" aria-hidden="true">
            <EditableToken path="penthouse.javascript_id" as="span">
              {javascriptId}
            </EditableToken>
            <EditableToken path="penthouse.config_id" as="span">
              {configId}
            </EditableToken>
          </div>
          <div className="rrac_pent_layout" />
        </aside>
      </div>

      {/* Schema-lint anchors (photos may unmount when empty outside edit mode) */}
      <EditableSlotGroup
        groupPath="penthouse.photo1"
        definition={photo1Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="penthouse.photo2"
        definition={photo2Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="penthouse.photo3"
        definition={photo3Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="penthouse.photo4"
        definition={photo4Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Spacing mirrors Tower 12:
           section 100px/40px; gallery mt 25px / mb 10px;
           features heading mt 20px; icons + list tucked tight. */
        .penthouse {
          background: #fff;
          padding: 3.25rem 1.25rem 3.5rem;
        }
        @media (min-width: 768px) {
          .penthouse { padding: 4.5rem 2rem 5rem; }
        }
        @media (min-width: 1200px) {
          .penthouse { padding: 100px 40px; }
        }

        .penthouse__layout {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          gap: 2rem;
          align-items: start;
        }
        @media (min-width: 992px) {
          .penthouse__layout {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 1.25rem 1.5rem;
          }
        }

        .penthouse__main {
          min-width: 0;
        }

        .penthouse-intro__heading-block {
          border-left: 10px solid #030303;
          padding: 0 0 0 10px;
          text-align: left;
          max-width: 36rem;
          margin: 0;
        }

        .penthouse-intro__eyebrow {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: 1.15rem;
          font-weight: 400;
          letter-spacing: 0.02em;
          line-height: 1.3;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
        }
        .penthouse-intro__eyebrow p { margin: 0; }
        @media (min-width: 768px) {
          .penthouse-intro__eyebrow { font-size: 1.45rem; }
        }

        .penthouse-intro__headline {
          margin: 0.125rem 0 0;
          font-family: var(--vibe-font-heading);
          font-size: 2.5rem;
          font-weight: 700;
          letter-spacing: 0;
          line-height: 1;
          text-transform: uppercase;
          color: #b6b6b6;
        }
        @media (min-width: 768px) {
          .penthouse-intro__headline { font-size: 3.75rem; }
        }
        @media (min-width: 1200px) {
          .penthouse-intro__headline { font-size: 5rem; }
        }

        .penthouse__gallery {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5px;
          margin: 25px 0 10px;
        }
        @media (min-width: 768px) {
          .penthouse__gallery {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
        }

        .penthouse__photo {
          display: block;
          position: relative;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          background: var(--vibe-color-surface);
        }
        .penthouse__photo > * {
          display: block;
          width: 100%;
          height: 100%;
        }
        .penthouse__photo-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .penthouse__photo-placeholder {
          width: 100%;
          height: 100%;
          background: var(--vibe-color-surface);
        }

        .penthouse-features__block {
          margin-top: 20px;
        }

        .penthouse-features__heading {
          margin: 0 0 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.12em;
          line-height: 1.4;
          text-align: center;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
        }
        @media (min-width: 768px) {
          .penthouse-features__heading {
            margin-bottom: 1.15rem;
            font-size: 0.875rem;
            letter-spacing: 0.14em;
          }
        }

        .penthouse__highlights {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.25rem 0.75rem;
          margin: 0 0 1.15rem;
        }
        @media (min-width: 768px) {
          .penthouse__highlights {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.75rem 0.5rem;
            margin-bottom: 1rem;
          }
        }

        .penthouse__highlight {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.55rem;
          color: #000;
          text-align: center;
        }
        .penthouse__highlight-media {
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
        }
        .penthouse__highlight:hover .penthouse__highlight-title {
          color: var(--vibe-color-secondary);
        }

        .penthouse__highlight-icon {
          display: block;
          width: auto;
          max-width: 5.5rem;
          height: 3.25rem;
          object-fit: contain;
          object-position: center;
        }
        @media (min-width: 768px) {
          .penthouse__highlight-icon {
            max-width: 6.25rem;
            height: 3.5rem;
          }
        }

        .penthouse__highlight-title {
          display: block;
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          line-height: 1.3;
          text-transform: uppercase;
          color: #000;
          transition: color 0.2s ease;
        }
        @media (min-width: 768px) {
          .penthouse__highlight-title { font-size: 0.75rem; }
        }

        .penthouse__feature-list {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-weight: 400;
          line-height: 1.65;
          color: #4b4d51;
        }
        @media (min-width: 768px) {
          .penthouse__feature-list {
            column-count: 2;
            column-gap: 2rem;
          }
        }
        .penthouse__feature-list p { margin: 0 0 0.5rem; }
        .penthouse__feature-list ul {
          list-style: disc;
          list-style-position: outside;
          margin: 0;
          padding: 0 0 0 1.15rem;
        }
        .penthouse__feature-list li {
          margin: 0 0 0.35rem;
          break-inside: avoid;
          color: #4b4d51;
        }
        .penthouse__feature-list li::marker { color: #4b4d51; }

        .penthouse__amenities-cta-wrap {
          display: flex;
          justify-content: center;
          margin: 1.75rem auto 0;
        }
        @media (min-width: 768px) {
          .penthouse__amenities-cta-wrap { margin-top: 2rem; }
        }

        .penthouse__amenities-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.65rem;
          min-width: 16.5rem;
          height: 60px;
          padding: 0 1.75rem;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .penthouse__amenities-cta:hover {
          background: #b6b6b6;
          color: #000;
        }
        .penthouse__amenities-cta-icon { flex-shrink: 0; }

        /* ── Right: ApartmentSync mount ── */
        .penthouse__aside {
          min-width: 0;
        }
        .penthouse__aside-meta {
          height: 0;
          overflow: hidden;
        }
        .penthouse__aside .rrac_pent_layout {
          width: 100%;
          min-height: 280px;
          background: #fff;
        }

        /* Scroll reveals */
        .penthouse-intro__heading-block,
        .penthouse-features__heading,
        .penthouse__feature-list,
        .penthouse__amenities-cta-wrap,
        .penthouse__aside {
          transition: opacity 1.1s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 1.1s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .penthouse.reveal--hidden .penthouse-intro__heading-block {
          opacity: 0;
          transform: translateX(-28px);
        }
        .penthouse.reveal--hidden .penthouse-features__heading,
        .penthouse.reveal--hidden .penthouse__feature-list {
          opacity: 0;
          transform: translateY(24px);
        }
        .penthouse.reveal--hidden .penthouse__amenities-cta-wrap {
          opacity: 0;
          transform: scale(0.65);
        }
        .penthouse.reveal--hidden .penthouse__aside {
          opacity: 0;
          transform: translateX(28px);
        }
        .penthouse.reveal--visible .penthouse-intro__heading-block,
        .penthouse.reveal--visible .penthouse-features__heading,
        .penthouse.reveal--visible .penthouse__feature-list,
        .penthouse.reveal--visible .penthouse__amenities-cta-wrap,
        .penthouse.reveal--visible .penthouse__aside {
          opacity: 1;
          transform: none;
        }
        .penthouse.reveal--visible .penthouse-features__heading { transition-delay: 0.1s; }
        .penthouse.reveal--visible .penthouse__feature-list { transition-delay: 0.2s; }
        .penthouse.reveal--visible .penthouse__amenities-cta-wrap { transition-delay: 0.3s; }
        .penthouse.reveal--visible .penthouse__aside { transition-delay: 0.15s; }

        .penthouse__gallery [data-reveal],
        .penthouse__highlights [data-reveal] {
          transition: opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .penthouse__gallery [data-reveal].reveal--hidden,
        .penthouse__highlights [data-reveal].reveal--hidden {
          opacity: 0;
          transform: translateY(24px);
        }
        .penthouse__gallery [data-reveal].reveal--visible,
        .penthouse__highlights [data-reveal].reveal--visible {
          opacity: 1;
          transform: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .penthouse-intro__heading-block,
          .penthouse-features__heading,
          .penthouse__feature-list,
          .penthouse__amenities-cta-wrap,
          .penthouse__aside,
          .penthouse__gallery [data-reveal],
          .penthouse__highlights [data-reveal],
          .penthouse.reveal--hidden .penthouse-intro__heading-block,
          .penthouse.reveal--hidden .penthouse-features__heading,
          .penthouse.reveal--hidden .penthouse__feature-list,
          .penthouse.reveal--hidden .penthouse__amenities-cta-wrap,
          .penthouse.reveal--hidden .penthouse__aside {
            opacity: 1;
            transform: none;
            transition: none;
          }
          .penthouse__amenities-cta { transition: none; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Penthouse',
  displayName: 'Penthouse',
  tokens: {
    'penthouse.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue:
        '<p>EXPERIENCE <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'penthouse.headline': {
      label: 'Headline',
      type: 'text',
      defaultValue: 'PENTHOUSE LIVING',
    },
    'penthouse.featuresHeading': {
      label: 'Features Heading',
      type: 'text',
      defaultValue: 'PENTHOUSE INTERIOR FEATURES',
    },
    'penthouse.highlight1Title': {
      label: 'Highlight 1 Title',
      type: 'text',
      defaultValue: 'Newly Designed Kitchens',
    },
    'penthouse.highlight2Title': {
      label: 'Highlight 2 Title',
      type: 'text',
      defaultValue: 'Stainless-Steel Appliances',
    },
    'penthouse.highlight3Title': {
      label: 'Highlight 3 Title',
      type: 'text',
      defaultValue: 'Ample Closet Space',
    },
    'penthouse.highlight4Title': {
      label: 'Highlight 4 Title',
      type: 'text',
      defaultValue: 'Private Patio or Balcony',
    },
    'penthouse.featureList': {
      label: 'Feature List',
      type: 'richtext',
      defaultValue:
        '<ul><li>Wine Fridge</li><li>Upgraded Appliances (Miele)</li><li>Waterfall Quartz Counters</li><li>Automated Roller Shades</li><li>Soaking Tub and Stand-up Shower with Double Showerheads</li><li>Unobstructed Views of Mt. Rainier and Puget Sound</li><li>Instant Hot-Water Dispenser</li><li>Custom California Closets</li><li>Oversized Balconies</li><li>6-Burner Gas Range</li><li>Under-cabinet Lighting</li><li>Laundry Room</li></ul>',
      generator: {
        prompt:
          'A bullet list of real penthouse or top-floor apartment interior features that actually exist at this property (finishes, appliances, views, outdoor space, storage). Only include features you can confirm; do not invent brand names or views. Prefer 8–14 concise items.',
        context:
          'Two-column feature bullet list under PENTHOUSE INTERIOR FEATURES on the Penthouse page, below the four icon highlights. An Explore Our Amenities CTA follows; an ApartmentSync plan card sits to the right.',
        length: '8–14 short bullet items',
      },
    },
    'penthouse.javascript_id': {
      label: 'JavaScript ID',
      type: 'text',
      defaultField: 'custom_apartmentsync_javascript_id',
      description:
        'Encoded token for the ApartmentSync script URL. Found in the Repli360 admin dashboard.',
    },
    'penthouse.config_id': {
      label: 'Config ID (Site ID)',
      type: 'text',
      defaultField: 'custom_apartmentsync_config_id',
      description:
        'ApartmentSync site identifier. Baked into the loaded script; stored here for reference.',
    },
  },
  slotGroups: {
    'penthouse.photo1': photo1Def,
    'penthouse.photo2': photo2Def,
    'penthouse.photo3': photo3Def,
    'penthouse.photo4': photo4Def,
    'penthouse.amenitiesCta': amenitiesCtaDef,
  },
};

(Penthouse as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Penthouse;
